import * as I from 'immutable';
import {PhoneNumber} from 'ApplicationEntry/models/PhoneNumber';
import {createAction} from 'Shared/utilities/actionCreator';
import * as CB from 'ApplicationEntry/models/ContingentBeneficiary';
import {ZipCode} from 'ApplicationEntry/models/ZipCode';

export const updateFirstName = (index: number, value: string) =>
  createAction(CB.ContingentBeneficiaryActionType.UPDATE_FIRST_NAME, {value, index});
export const updateMiddleName = (index: number, value: string) =>
  createAction(CB.ContingentBeneficiaryActionType.UPDATE_MIDDLE_NAME, {value, index});
export const updateLastName = (index: number, value: string) =>
  createAction(CB.ContingentBeneficiaryActionType.UPDATE_LAST_NAME, {value, index});
export const updateRelationship = (index: number, value: string) =>
  createAction(CB.ContingentBeneficiaryActionType.UPDATE_RELATIONSHIP, {value, index});
export const updatePercent = (index: number, value: number) =>
  createAction(CB.ContingentBeneficiaryActionType.UPDATE_PERCENT, {value, index});
export const updatePhone = (index: number, value: PhoneNumber) =>
  createAction(CB.ContingentBeneficiaryActionType.UPDATE_PHONE, {value, index});
export const updateDOB = (index: number, value: Date | '') =>
  createAction(CB.ContingentBeneficiaryActionType.UPDATE_DOB, {value, index});
export const updateSSN = (index: number, value: string) =>
  createAction(CB.ContingentBeneficiaryActionType.UPDATE_SSN, {value, index});
export const updateAddress = (index: number, value: string) =>
  createAction(CB.ContingentBeneficiaryActionType.UPDATE_ADDRESS, {value, index});
export const updateAddressLine2 = (index: number, value: string) =>
  createAction(CB.ContingentBeneficiaryActionType.UPDATE_ADDRESS_LINE2, {value, index});
export const updateCity = (index: number, value: string) =>
  createAction(CB.ContingentBeneficiaryActionType.UPDATE_CITY, {value, index});
export const updateState = (index: number, value: string) =>
  createAction(CB.ContingentBeneficiaryActionType.UPDATE_STATE, {value, index});
export const updateZip = (index: number, value: ZipCode) =>
  createAction(CB.ContingentBeneficiaryActionType.UPDATE_ZIP, {value, index});
export const addContingentBeneficiary = () =>
  createAction(CB.ContingentBeneficiaryActionType.ADD_CONTINGENTBENEFICIARY);
export const removeContingentBeneficiary = (index: number) =>
  createAction(CB.ContingentBeneficiaryActionType.REMOVE_CONTINGENTBENEFICIARY, {index});
export const emptyContingentBeneficiary = () =>
  createAction(CB.ContingentBeneficiaryActionType.EMPTY_CONTINGENTBENEFICIARY);
export const editContingentBeneficiary = (contingentBeneficiary: CB.ContingentBeneficiary) =>
  createAction(CB.ContingentBeneficiaryActionType.EDIT_CONTINGENTBENEFICIARY, {contingentBeneficiary});
export const updateSignatureDate = (index: number, value: Date | '') =>
  createAction(CB.ContingentBeneficiaryActionType.UPDATE_SIGNATURE_DATE, {value, index});

export type Action =
  | ReturnType<typeof updateFirstName>
  | ReturnType<typeof updateMiddleName>
  | ReturnType<typeof updateLastName>
  | ReturnType<typeof updateRelationship>
  | ReturnType<typeof updatePercent>
  | ReturnType<typeof updatePhone>
  | ReturnType<typeof updateDOB>
  | ReturnType<typeof updateSSN>
  | ReturnType<typeof updateAddress>
  | ReturnType<typeof updateAddressLine2>
  | ReturnType<typeof updateCity>
  | ReturnType<typeof updateState>
  | ReturnType<typeof updateZip>
  | ReturnType<typeof addContingentBeneficiary>
  | ReturnType<typeof removeContingentBeneficiary>
  | ReturnType<typeof emptyContingentBeneficiary>
  | ReturnType<typeof editContingentBeneficiary>
  | ReturnType<typeof updateSignatureDate>;

export type State = {members: I.List<CB.ContingentBeneficiary>};
const DEFAULT_STATE: State = {members: I.List<CB.ContingentBeneficiary>().push(CB.EmptyContingentBeneficiary)};

export function reducer(state: State = DEFAULT_STATE, action: Action): State {
  const newState = {...state};
  let newContingentBeneficiary;
  let prevContingentBeneficiary: CB.ContingentBeneficiary;
  switch (action.type) {
    case CB.ContingentBeneficiaryActionType.ADD_CONTINGENTBENEFICIARY:
      newState.members = state.members.push(CB.EmptyContingentBeneficiary);
      return {members: newState.members};
    case CB.ContingentBeneficiaryActionType.REMOVE_CONTINGENTBENEFICIARY:
      newState.members = state.members.delete(action.payload.index);
      return {members: newState.members};
    case CB.ContingentBeneficiaryActionType.UPDATE_FIRST_NAME:
      prevContingentBeneficiary = state.members.get(action.payload.index) as CB.ContingentBeneficiary;
      newContingentBeneficiary = CB.changeFirstName(action.payload.value)(prevContingentBeneficiary);
      newState.members = state.members.splice(action.payload.index, 1, newContingentBeneficiary);
      return {members: newState.members};
    case CB.ContingentBeneficiaryActionType.UPDATE_MIDDLE_NAME:
      prevContingentBeneficiary = state.members.get(action.payload.index) as CB.ContingentBeneficiary;
      newContingentBeneficiary = CB.changeMiddleName(action.payload.value)(prevContingentBeneficiary);
      newState.members = state.members.splice(action.payload.index, 1, newContingentBeneficiary);
      return {members: newState.members};
    case CB.ContingentBeneficiaryActionType.UPDATE_LAST_NAME:
      prevContingentBeneficiary = state.members.get(action.payload.index) as CB.ContingentBeneficiary;
      newContingentBeneficiary = CB.changeLastName(action.payload.value)(prevContingentBeneficiary);
      newState.members = state.members.splice(action.payload.index, 1, newContingentBeneficiary);
      return {members: newState.members};
    case CB.ContingentBeneficiaryActionType.UPDATE_PERCENT:
      prevContingentBeneficiary = state.members.get(action.payload.index) as CB.ContingentBeneficiary;
      newContingentBeneficiary = CB.changePercent(action.payload.value as number)(prevContingentBeneficiary);
      newState.members = state.members.splice(action.payload.index, 1, newContingentBeneficiary);
      return {members: newState.members};
    case CB.ContingentBeneficiaryActionType.UPDATE_SSN:
      prevContingentBeneficiary = state.members.get(action.payload.index) as CB.ContingentBeneficiary;
      newContingentBeneficiary = CB.changeSSN(action.payload.value)(prevContingentBeneficiary);
      newState.members = state.members.splice(action.payload.index, 1, newContingentBeneficiary);
      return {members: newState.members};
    case CB.ContingentBeneficiaryActionType.UPDATE_RELATIONSHIP:
      prevContingentBeneficiary = state.members.get(action.payload.index) as CB.ContingentBeneficiary;
      newContingentBeneficiary = CB.changeRelationship(action.payload.value)(prevContingentBeneficiary);
      newState.members = state.members.splice(action.payload.index, 1, newContingentBeneficiary);
      return {members: newState.members};
    case CB.ContingentBeneficiaryActionType.UPDATE_DOB:
      prevContingentBeneficiary = state.members.get(action.payload.index) as CB.ContingentBeneficiary;
      newContingentBeneficiary = CB.changeDOB(action.payload.value)(prevContingentBeneficiary);
      newState.members = state.members.splice(action.payload.index, 1, newContingentBeneficiary);
      return {members: newState.members};
    case CB.ContingentBeneficiaryActionType.UPDATE_PHONE:
      prevContingentBeneficiary = state.members.get(action.payload.index) as CB.ContingentBeneficiary;
      newContingentBeneficiary = CB.changePhone(action.payload.value)(prevContingentBeneficiary);
      newState.members = state.members.splice(action.payload.index, 1, newContingentBeneficiary);
      return {members: newState.members};
    case CB.ContingentBeneficiaryActionType.UPDATE_ADDRESS:
      prevContingentBeneficiary = state.members.get(action.payload.index) as CB.ContingentBeneficiary;
      newContingentBeneficiary = CB.changeAddress(action.payload.value)(prevContingentBeneficiary);
      newState.members = state.members.splice(action.payload.index, 1, newContingentBeneficiary);
      return {members: newState.members};
    case CB.ContingentBeneficiaryActionType.UPDATE_ADDRESS_LINE2:
      prevContingentBeneficiary = state.members.get(action.payload.index) as CB.ContingentBeneficiary;
      newContingentBeneficiary = CB.changeAddressLine2(action.payload.value)(prevContingentBeneficiary);
      newState.members = state.members.splice(action.payload.index, 1, newContingentBeneficiary);
      return {members: newState.members};
    case CB.ContingentBeneficiaryActionType.UPDATE_CITY:
      prevContingentBeneficiary = state.members.get(action.payload.index) as CB.ContingentBeneficiary;
      newContingentBeneficiary = CB.changeCity(action.payload.value)(prevContingentBeneficiary);
      newState.members = state.members.splice(action.payload.index, 1, newContingentBeneficiary);
      return {members: newState.members};
    case CB.ContingentBeneficiaryActionType.UPDATE_STATE:
      prevContingentBeneficiary = state.members.get(action.payload.index) as CB.ContingentBeneficiary;
      newContingentBeneficiary = CB.changeState(action.payload.value)(prevContingentBeneficiary);
      newState.members = state.members.splice(action.payload.index, 1, newContingentBeneficiary);
      return {members: newState.members};
    case CB.ContingentBeneficiaryActionType.UPDATE_ZIP:
      prevContingentBeneficiary = state.members.get(action.payload.index) as CB.ContingentBeneficiary;
      newContingentBeneficiary = CB.changeZip(action.payload.value)(prevContingentBeneficiary);
      newState.members = state.members.splice(action.payload.index, 1, newContingentBeneficiary);
      return {members: newState.members};
    case CB.ContingentBeneficiaryActionType.UPDATE_SIGNATURE_DATE:
      prevContingentBeneficiary = state.members.get(action.payload.index) as CB.ContingentBeneficiary;
      newContingentBeneficiary = CB.changeSignatureDate(action.payload.value)(prevContingentBeneficiary);
      newState.members = state.members.splice(action.payload.index, 1, newContingentBeneficiary);
      return { members: newState.members };
    case CB.ContingentBeneficiaryActionType.EMPTY_CONTINGENTBENEFICIARY:
      return {members: I.List<CB.ContingentBeneficiary>().push(CB.EmptyContingentBeneficiary)};
    case CB.ContingentBeneficiaryActionType.EDIT_CONTINGENTBENEFICIARY:
      return {members: I.List<CB.ContingentBeneficiary>().push(action.payload.contingentBeneficiary)};
    default:
      return state;
  }
}
